import { supabase } from "~/integrations/supabase/client";
import type {
  ReturnInspection,
  ReturnInspectionFormData,
  ReturnPutawayFormData,
  ReturnReceiveFormData,
  ReturnRequest,
  ReturnRequestFilters,
  ReturnRequestFormData,
  ReturnRequestItem,
  ReturnRequestStatus,
  ReturnStatistics,
} from "~/types/returns";

// Type assertion for WMS tables that exist in dev branch but not yet in generated types
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const wmsSupabase = supabase as any;

export interface ReturnRequestFiltersExtended extends ReturnRequestFilters {
  limit?: number;
  offset?: number;
}

export const returnService = {
  // ============================================================================
  // Return Requests
  // ============================================================================

  async getReturnRequests(
    filters?: ReturnRequestFiltersExtended
  ): Promise<ReturnRequest[]> {
    let query = wmsSupabase
      .from("wms_return_requests")
      .select(
        `
        *,
        client:clients(id, name, tax_id),
        original_sales_request:sales_request(id, created_at),
        assigned_profile:profiles!wms_return_requests_assigned_to_fkey(name, lastname),
        items:wms_return_request_items(
          *,
          product:products(id, short_name, long_name),
          original_batch:production_batches(id, batch_number, expiry_date)
        )
      `
      )
      .order("created_at", { ascending: false });

    if (filters?.status) {
      if (Array.isArray(filters.status)) {
        query = query.in("status", filters.status);
      } else {
        query = query.eq("status", filters.status);
      }
    }

    if (filters?.client_id) {
      query = query.eq("client_id", filters.client_id);
    }

    if (filters?.assigned_to) {
      query = query.eq("assigned_to", filters.assigned_to);
    }

    if (filters?.return_type) {
      query = query.eq("return_type", filters.return_type);
    }

    if (filters?.priority) {
      query = query.eq("priority", filters.priority);
    }

    if (filters?.date_from) {
      query = query.gte("created_at", filters.date_from);
    }

    if (filters?.date_to) {
      query = query.lte("created_at", filters.date_to);
    }

    if (filters?.limit) {
      query = query.limit(filters.limit);
    }

    if (filters?.offset) {
      query = query.range(
        filters.offset,
        filters.offset + (filters.limit || 50) - 1
      );
    }

    const { data, error } = await query;
    if (error) throw error;
    return data as ReturnRequest[];
  },

  async getReturnRequestById(id: string): Promise<ReturnRequest> {
    const { data, error } = await wmsSupabase
      .from("wms_return_requests")
      .select(
        `
        *,
        client:clients(id, name, tax_id),
        original_sales_request:sales_request(id, created_at),
        assigned_profile:profiles!wms_return_requests_assigned_to_fkey(name, lastname),
        items:wms_return_request_items(
          *,
          product:products(id, short_name, long_name),
          original_batch:production_batches(id, batch_number, expiry_date),
          inspections:wms_return_inspections(
            *,
            destination_warehouse:wms_warehouses(id, code, name),
            destination_position:wms_positions(id, full_code, barcode),
            inspector:profiles(name, lastname)
          )
        )
      `
      )
      .eq("id", id)
      .single();

    if (error) throw error;
    return data as ReturnRequest;
  },

  async createReturnRequest(
    formData: ReturnRequestFormData
  ): Promise<ReturnRequest> {
    // First, generate a return number
    const { data: returnNumberResult, error: rnError } = await wmsSupabase.rpc(
      "generate_return_number"
    );

    if (rnError) throw rnError;

    // Create the return request
    const { data: returnRequest, error: requestError } = await wmsSupabase
      .from("wms_return_requests")
      .insert({
        return_number: returnNumberResult,
        credit_note_id: formData.credit_note_id || null,
        credit_note_number: formData.credit_note_number || null,
        original_sales_request_id: formData.original_sales_request_id || null,
        client_id: formData.client_id || null,
        return_type: formData.return_type,
        priority: formData.priority || 2,
        notes: formData.notes || null,
        status: "pending",
      })
      .select()
      .single();

    if (requestError) throw requestError;

    // Create the items
    if (formData.items && formData.items.length > 0) {
      const itemsToInsert = formData.items.map((item) => ({
        return_request_id: returnRequest.id,
        product_id: item.product_id,
        expected_quantity: item.expected_quantity,
        received_quantity: 0,
        return_reason: item.return_reason,
        return_reason_notes: item.return_reason_notes || null,
        original_batch_id: item.original_batch_id || null,
        original_lot_number: item.original_lot_number || null,
        status: "pending",
      }));

      const { error: itemsError } = await supabase
        .from("wms_return_request_items")
        .insert(itemsToInsert);

      if (itemsError) throw itemsError;
    }

    return this.getReturnRequestById(returnRequest.id);
  },

  async claimReturnRequest(id: string, userId: string): Promise<ReturnRequest> {
    const { data, error } = await wmsSupabase
      .from("wms_return_requests")
      .update({
        assigned_to: userId,
        assigned_at: new Date().toISOString(),
        status: "receiving",
        started_at: new Date().toISOString(),
      })
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    return data as ReturnRequest;
  },

  async updateReturnRequestStatus(
    id: string,
    status: ReturnRequestStatus,
    additionalData?: {
      completed_at?: string;
      notes?: string;
      internal_notes?: string;
    }
  ): Promise<ReturnRequest> {
    const updateData: Record<string, unknown> = {
      status,
      updated_at: new Date().toISOString(),
      ...additionalData,
    };

    if (status === "completed" && !additionalData?.completed_at) {
      updateData.completed_at = new Date().toISOString();
    }

    const { data, error } = await wmsSupabase
      .from("wms_return_requests")
      .update(updateData)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    return data as ReturnRequest;
  },

  // ============================================================================
  // Return Request Items
  // ============================================================================

  async getReturnRequestItems(
    returnRequestId: string
  ): Promise<ReturnRequestItem[]> {
    const { data, error } = await wmsSupabase
      .from("wms_return_request_items")
      .select(
        `
        *,
        product:products(id, short_name, long_name),
        original_batch:production_batches(id, batch_number, expiry_date),
        inspections:wms_return_inspections(
          *,
          destination_warehouse:wms_warehouses(id, code, name),
          destination_position:wms_positions(id, full_code, barcode)
        )
      `
      )
      .eq("return_request_id", returnRequestId)
      .order("created_at", { ascending: true });

    if (error) throw error;
    return data as ReturnRequestItem[];
  },

  async receiveReturnItem(
    formData: ReturnReceiveFormData,
    userId: string
  ): Promise<ReturnRequestItem> {
    const { data, error } = await wmsSupabase
      .from("wms_return_request_items")
      .update({
        received_quantity: formData.received_quantity,
        status: "received",
        updated_at: new Date().toISOString(),
      })
      .eq("id", formData.return_request_item_id)
      .select(
        `
        *,
        product:products(id, short_name, long_name),
        original_batch:production_batches(id, batch_number, expiry_date)
      `
      )
      .single();

    if (error) throw error;

    // Record the movement
    const item = data as ReturnRequestItem;

    // Get the return request to reference it
    const { data: itemWithRequest } = await wmsSupabase
      .from("wms_return_request_items")
      .select("return_request_id")
      .eq("id", formData.return_request_item_id)
      .single();

    if (itemWithRequest) {
      await wmsSupabase.from("wms_movements").insert({
        movement_type: "return_receipt",
        product_id: item.product_id,
        lot_number: item.original_lot_number || null,
        box_quantity: formData.received_quantity,
        reference_type: "return_request",
        reference_id: itemWithRequest.return_request_id,
        notes: formData.notes || null,
        performed_by: userId,
        performed_at: new Date().toISOString(),
      });
    }

    return item;
  },

  async updateItemStatus(
    itemId: string,
    status: ReturnRequestItem["status"]
  ): Promise<ReturnRequestItem> {
    const { data, error } = await wmsSupabase
      .from("wms_return_request_items")
      .update({
        status,
        updated_at: new Date().toISOString(),
      })
      .eq("id", itemId)
      .select()
      .single();

    if (error) throw error;
    return data as ReturnRequestItem;
  },

  // ============================================================================
  // Inspections
  // ============================================================================

  async createInspection(
    formData: ReturnInspectionFormData,
    userId: string
  ): Promise<ReturnInspection> {
    const { data, error } = await wmsSupabase
      .from("wms_return_inspections")
      .insert({
        return_request_item_id: formData.return_request_item_id,
        disposition: formData.disposition,
        quantity: formData.quantity,
        discard_reason: formData.discard_reason || null,
        destination_warehouse_id: formData.destination_warehouse_id || null,
        notes: formData.notes || null,
        photo_url: formData.photo_url || null,
        inspected_by: userId,
        inspected_at: new Date().toISOString(),
      })
      .select(
        `
        *,
        destination_warehouse:wms_warehouses(id, code, name),
        destination_position:wms_positions(id, full_code, barcode),
        inspector:profiles(name, lastname)
      `
      )
      .single();

    if (error) throw error;

    // Update item status to inspected
    await this.updateItemStatus(formData.return_request_item_id, "inspected");

    return data as ReturnInspection;
  },

  async routeInspection(
    formData: ReturnPutawayFormData,
    userId: string
  ): Promise<ReturnInspection> {
    // Get inspection details
    const { data: inspection, error: inspError } = await wmsSupabase
      .from("wms_return_inspections")
      .select(
        `
        *,
        return_request_item:wms_return_request_items(
          *,
          return_request_id,
          product_id,
          original_lot_number,
          original_batch:production_batches(expiry_date)
        )
      `
      )
      .eq("id", formData.inspection_id)
      .single();

    if (inspError) throw inspError;

    // Update inspection with destination
    const { data: updatedInspection, error: updateError } = await wmsSupabase
      .from("wms_return_inspections")
      .update({
        destination_position_id: formData.destination_position_id,
        routed_at: new Date().toISOString(),
      })
      .eq("id", formData.inspection_id)
      .select(
        `
        *,
        destination_warehouse:wms_warehouses(id, code, name),
        destination_position:wms_positions(id, full_code, barcode)
      `
      )
      .single();

    if (updateError) throw updateError;

    const item = inspection.return_request_item;

    // Determine movement type based on disposition
    const movementTypeMap: Record<string, string> = {
      SELLABLE: "return_sellable",
      REPACKAGE: "return_repackage",
      DISCARD: "return_discard",
      SUPPLIER_RETURN: "return_receipt",
    };

    // Record the movement
    await wmsSupabase.from("wms_movements").insert({
      movement_type: movementTypeMap[inspection.disposition],
      product_id: item.product_id,
      lot_number: item.original_lot_number || null,
      box_quantity: inspection.quantity,
      to_position_id: formData.destination_position_id,
      reference_type: "return_request",
      reference_id: item.return_request_id,
      performed_by: userId,
      performed_at: new Date().toISOString(),
    });

    // If SELLABLE, create inventory record
    if (inspection.disposition === "SELLABLE") {
      await wmsSupabase.from("wms_inventory").insert({
        position_id: formData.destination_position_id,
        product_id: item.product_id,
        production_batch_id: item.original_batch_id || null,
        lot_number: item.original_lot_number || null,
        box_count: inspection.quantity,
        reserved_boxes: 0,
        expiration_date: item.original_batch?.expiry_date || null,
        received_at: new Date().toISOString(),
        is_return: true,
        return_request_id: item.return_request_id,
        returned_at: new Date().toISOString(),
        original_sales_request_id: null, // Could be fetched from return request if needed
      });
    }

    // If DISCARD, create writeoff movement
    if (inspection.disposition === "DISCARD") {
      await wmsSupabase.from("wms_movements").insert({
        movement_type: "writeoff",
        product_id: item.product_id,
        lot_number: item.original_lot_number || null,
        box_quantity: inspection.quantity,
        to_position_id: formData.destination_position_id,
        reference_type: "return_request",
        reference_id: item.return_request_id,
        reason: inspection.discard_reason || null,
        notes: inspection.notes || null,
        performed_by: userId,
        performed_at: new Date().toISOString(),
      });
    }

    // Update item status to routed
    await this.updateItemStatus(item.id, "routed");

    return updatedInspection as ReturnInspection;
  },

  async getInspectionsByReturnRequest(
    returnRequestId: string
  ): Promise<ReturnInspection[]> {
    const { data, error } = await wmsSupabase
      .from("wms_return_inspections")
      .select(
        `
        *,
        destination_warehouse:wms_warehouses(id, code, name),
        destination_position:wms_positions(id, full_code, barcode),
        inspector:profiles(name, lastname),
        return_request_item:wms_return_request_items(
          id,
          return_request_id,
          product:products(id, short_name)
        )
      `
      )
      .eq("return_request_item.return_request_id", returnRequestId);

    if (error) throw error;
    return data as ReturnInspection[];
  },

  // ============================================================================
  // Statistics
  // ============================================================================

  async getReturnStatistics(): Promise<ReturnStatistics> {
    const today = new Date().toISOString().split("T")[0];

    const [
      pending,
      receiving,
      inspecting,
      routing,
      completedToday,
      pendingItems,
    ] = await Promise.all([
      supabase
        .from("wms_return_requests")
        .select("id", { count: "exact", head: true })
        .eq("status", "pending"),
      supabase
        .from("wms_return_requests")
        .select("id", { count: "exact", head: true })
        .eq("status", "receiving"),
      supabase
        .from("wms_return_requests")
        .select("id", { count: "exact", head: true })
        .eq("status", "inspecting"),
      supabase
        .from("wms_return_requests")
        .select("id", { count: "exact", head: true })
        .eq("status", "routing"),
      supabase
        .from("wms_return_requests")
        .select("id", { count: "exact", head: true })
        .eq("status", "completed")
        .gte("completed_at", `${today}T00:00:00`),
      supabase
        .from("wms_return_request_items")
        .select("expected_quantity")
        .in("status", ["pending", "received"]),
    ]);

    // Get disposition breakdown
    const { data: dispositions } = await wmsSupabase
      .from("wms_return_inspections")
      .select("disposition, quantity");

    const dispositionBreakdown = {
      sellable: 0,
      repackage: 0,
      discard: 0,
      supplier_return: 0,
    };

    if (dispositions) {
      for (const d of dispositions) {
        switch (d.disposition) {
          case "SELLABLE":
            dispositionBreakdown.sellable += d.quantity;
            break;
          case "REPACKAGE":
            dispositionBreakdown.repackage += d.quantity;
            break;
          case "DISCARD":
            dispositionBreakdown.discard += d.quantity;
            break;
          case "SUPPLIER_RETURN":
            dispositionBreakdown.supplier_return += d.quantity;
            break;
        }
      }
    }

    const totalItemsPending = (pendingItems.data || []).reduce(
      (sum, item) => sum + (item.expected_quantity || 0),
      0
    );

    return {
      pending_returns: pending.count || 0,
      receiving_returns: receiving.count || 0,
      inspecting_returns: inspecting.count || 0,
      routing_returns: routing.count || 0,
      completed_today: completedToday.count || 0,
      total_items_pending: totalItemsPending,
      disposition_breakdown: dispositionBreakdown,
    };
  },

  // ============================================================================
  // Helper: Check if all items are routed to complete return request
  // ============================================================================

  async checkAndCompleteReturnRequest(
    returnRequestId: string
  ): Promise<boolean> {
    const items = await this.getReturnRequestItems(returnRequestId);

    const allRouted = items.every((item) => item.status === "routed");

    if (allRouted) {
      await this.updateReturnRequestStatus(returnRequestId, "completed");
      return true;
    }

    return false;
  },

  // ============================================================================
  // Get warehouses by type (for routing)
  // ============================================================================

  async getWarehousesByType(
    warehouseType: string
  ): Promise<Array<{ id: string; code: string; name: string }>> {
    const { data, error } = await wmsSupabase
      .from("wms_warehouses")
      .select("id, code, name")
      .eq("warehouse_type", warehouseType)
      .eq("is_active", true)
      .order("name");

    if (error) throw error;
    return data || [];
  },
};
